import { useState, useCallback } from 'react';
import { getSubscriptionsApi } from '@/services/bolNotificationsApiClient';
import { 
  Subscription, 
  SubscriptionCreateRequest, 
  SubscriptionPatchRequest 
} from '@/api/bol-notifications';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';

export interface UseSubscriptionsReturn {
  subscriptions: Subscription[];
  loading: boolean;
  selectedSubscription: Subscription | null;
  setSelectedSubscription: (subscription: Subscription | null) => void;
  fetchSubscriptions: (page?: number, pageSize?: number) => Promise<void>;
  createSubscription: (subscriptionData: SubscriptionCreateRequest) => Promise<void>;
  updateSubscription: (subscriptionId: string, subscriptionData: SubscriptionPatchRequest) => Promise<void>;
  deleteSubscription: (subscriptionId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useSubscriptions = (): UseSubscriptionsReturn => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [total, setTotal] = useState(0);
  
  const { showSuccess, showError } = useSnackbarNotification();

  const fetchSubscriptions = useCallback(async (page: number = 0, pageSize: number = 10) => {
    console.log('Fetching subscriptions with:', { page, pageSize });

    setLoading(true);
    
    try {
      const api = getSubscriptionsApi();
      
      const response = await api.listSubscriptions({
        limit: pageSize,
        offset: page * pageSize
      });
      
      console.log('Subscriptions API response:', response);
      
      setSubscriptions(response.results);
      setTotal(response.pagination.total);
    } catch (err: unknown) {
      console.error('Error fetching subscriptions:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscriptions';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createSubscription = useCallback(async (subscriptionData: SubscriptionCreateRequest) => {
    try {
      console.log('Creating subscription with data:', subscriptionData);
      
      const api = getSubscriptionsApi();
      
      const createdSubscription = await api.createSubscription({
        contentType: 'application/json',
        subscriptionCreateRequest: subscriptionData,
        source: true
      });
      
      console.log('Subscription created successfully:', createdSubscription);
      
      showSuccess('Subscription created successfully');
    } catch (err: unknown) {
      console.error('Error creating subscription:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create subscription';
      showError(`Create subscription failed: ${errorMessage}`);
      throw err;
    }
  }, [showSuccess, showError]);

  const updateSubscription = useCallback(async (subscriptionId: string, subscriptionData: SubscriptionPatchRequest) => {
    try {
      console.log('Updating subscription with data:', subscriptionData);
      
      const api = getSubscriptionsApi();
      
      const updatedSubscription = await api.patchSubscriptionById({
        contentType: 'application/json',
        subscriptionId: subscriptionId,
        subscriptionPatchRequest: subscriptionData
      });
      
      console.log('Subscription updated successfully:', updatedSubscription);
      
      showSuccess('Subscription updated successfully');
    } catch (err: unknown) {
      console.error('Error updating subscription:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update subscription';
      showError(`Update subscription failed: ${errorMessage}`);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteSubscription = useCallback(async (subscriptionId: string) => {
    try {
      const api = getSubscriptionsApi();
      
      await api.deleteSubscriptionById({
        subscriptionId: subscriptionId
      });
      
      showSuccess('Subscription deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting subscription:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete subscription';
      showError(`Delete subscription failed: ${errorMessage}`);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    subscriptions,
    loading,
    selectedSubscription,
    setSelectedSubscription,
    fetchSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    setTotal,
    total
  };
};